import type { RunReportMutation } from "~/gql/mutations/reports.generated";
import type { MutationRawResult } from "~/helpers/runMutation";

import { RunReportOutcome } from "~/gql/graphqlBase.generated";
import isRecord from "~/helpers/isRecord";

export interface ReportRunAttempt {
	executionId: string;
	parameters: string;
	reportId: string;
	reportVersion: number;
}

export interface ReportRunArtifact {
	artifactSha256: string;
	executionId: string;
	pdfBase64: string;
}

export interface UnsettledReportRun {
	attempt: ReportRunAttempt;
	kind: "unconfirmed" | "unknown";
}

export type ReportRunResult =
	| { artifact: null | ReportRunArtifact; executionId: string; kind: "recorded" }
	| { artifact: null | ReportRunArtifact; kind: "unconfirmed" }
	| { kind: "refused"; messages: string[] }
	| { kind: "unknown" };

const RECORDED_OUTCOMES: ReadonlySet<null | RunReportOutcome | undefined> = new Set([
	RunReportOutcome.AlreadyRecorded,
	RunReportOutcome.GenerationFailed,
	RunReportOutcome.Recorded
]);

export const buildReportRunPath = (executionId: string): string =>
	`/reports/executions/${encodeURIComponent(executionId)}`;

export const runParameterEntries = (parameters: null | string | undefined): Array<[string, string]> | null => {
	if (!parameters) return [];
	try {
		const parsed: unknown = JSON.parse(parameters);
		if (!isRecord(parsed)) return null;
		return Object.entries(parsed).map(([name, value]) => [
			name,
			typeof value === "string" ? value : JSON.stringify(value)
		]);
	} catch {
		return null;
	}
};

const classifyReportRun = (attempt: ReportRunAttempt, raw: MutationRawResult<RunReportMutation>): ReportRunResult => {
	const payload = raw.data?.runReport;
	if (!payload) return { kind: "unknown" };
	const artifact =
		payload.pdfBase64 && payload.artifactSha256
			? { artifactSha256: payload.artifactSha256, executionId: attempt.executionId, pdfBase64: payload.pdfBase64 }
			: null;
	if (payload.outcome === RunReportOutcome.RecordUnconfirmed) return { artifact, kind: "unconfirmed" };
	if (RECORDED_OUTCOMES.has(payload.outcome)) {
		return payload.reportExecution?.executionId === attempt.executionId
			? { artifact, executionId: attempt.executionId, kind: "recorded" }
			: { kind: "unknown" };
	}
	if (raw.transportError || !payload.errors?.length) return { kind: "unknown" };
	return { kind: "refused", messages: payload.errors.map((error) => error.message) };
};

export default classifyReportRun;
